// src/features/simulators/dispenser/hooks/useDispenserSimulation.ts

import { useCallback } from 'react';
import type { AnalyticsClient } from '../../shared/utils/analyticsClient';
import type { LogEntry } from '../../shared/types/simulator.types';
import type { SimulationSummary } from '../../shared/hooks/useSimulatorWizard';
import type { DispenserConfigValues } from '../components/DispenserConfigForm';
import {
  QRCodeType,
  DispenserErrorCode,
  CheckInSource,
  DISPENSER_EVENT_CODES,
} from '../types';
import {
  generateReservationNumber,
  generateRoomNumber,
  generateGuestName,
  generateDocument,
  randomPick,
  generateAlternateLocators,
} from '../../shared/utils/dataGenerators';

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const ERROR_RATE = 0.05;

const getRandomErrorCode = (): DispenserErrorCode => {
  return randomPick([
    DispenserErrorCode.MECH_JAM,
    DispenserErrorCode.OUT_OF_KEYS,
    DispenserErrorCode.SENSOR_ERROR,
    DispenserErrorCode.TIMEOUT,
    DispenserErrorCode.DOOR_OPEN,
  ]);
};

export const useDispenserSimulation = () => {
  const simulate = useCallback(async (
    client: AnalyticsClient,
    addLog: LogFn,
    deviceId: string,
    config: DispenserConfigValues,
  ): Promise<SimulationSummary> => {
    const startTime = Date.now();
    let totalEvents = 0;
    let successful = 0;
    let failed = 0;

    const isWalkingGuest = config.qrCodeType === QRCodeType.ROOM_NUMBER;

    const reservationNumber = !isWalkingGuest
      ? (config.autoGenerateReservation
          ? generateReservationNumber()
          : (config.reservationNumber || generateReservationNumber()))
      : undefined;

    const roomNumber = generateRoomNumber();
    const willFail = Math.random() < ERROR_RATE;
    const alternateLocators = !isWalkingGuest ? generateAlternateLocators() : undefined;
    const qrValue = isWalkingGuest ? roomNumber : reservationNumber;

    addLog('info', `QR scanned: ${isWalkingGuest ? 'Walking guest' : 'Reservation'} | Code: ${qrValue} | Room: ${roomNumber}`);

    // QR scan delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // QR read error (more likely for room-only QR codes)
    if (isWalkingGuest && Math.random() < 0.02) {
      await client.sendEvent({
        eventCode: DISPENSER_EVENT_CODES.ERROR,
        deviceId,
        sectionCode: 'K',
        errorCode: DispenserErrorCode.QR_READ_ERROR,
        metadata: {
          qrCodeType: config.qrCodeType,
          roomNumber,
          isWalkingGuest: 'true',
        },
      });
      totalEvents++;
      failed++;

      addLog('error', `QR read error for room ${roomNumber}`);

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned: 0,
        duration: Date.now() - startTime,
        error: 'QR code read error',
      };
    }

    // PMS Check-in if dispenser does it
    if (config.performPMSCheckIn && config.checkInSource === CheckInSource.DISPENSER) {
      const guestData = generateGuestName();
      const documentData = generateDocument();

      await client.sendEvent({
        eventCode: 'CI_COMP',
        deviceId,
        reservationNumber,
        roomNumber,
        alternateReservationNumbers: alternateLocators,
        metadata: {
          roomNumber,
          guestName: guestData.full,
          document: documentData.full,
          checkInSource: config.checkInSource,
          channel: 'Dispenser',
          checkInType: isWalkingGuest ? 'Walking' : 'Reservation',
          isWalkingGuest: isWalkingGuest.toString(),
        },
      });
      totalEvents++;

      addLog('success', `Check-in completed for ${isWalkingGuest ? 'walking guest' : reservationNumber} - Room: ${roomNumber}`);
      await new Promise(resolve => setTimeout(resolve, 800));
    }

    // Key dispense delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (willFail) {
      const errorCode = getRandomErrorCode();

      await client.sendEvent({
        eventCode: DISPENSER_EVENT_CODES.ERROR,
        deviceId,
        reservationNumber,
        roomNumber,
        alternateReservationNumbers: alternateLocators,
        sectionCode: 'K',
        errorCode,
        metadata: {
          roomNumber,
          qrCodeType: config.qrCodeType,
          checkInSource: config.checkInSource,
          isWalkingGuest: isWalkingGuest.toString(),
        },
      });
      totalEvents++;
      failed++;

      addLog('error', `Dispense failed: ${errorCode} | Room: ${roomNumber}`);

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned: 0,
        duration: Date.now() - startTime,
        error: errorCode,
      };
    }

    // Success - Key dispensed
    await client.sendEvent({
      eventCode: DISPENSER_EVENT_CODES.KEY_DISPENSED,
      deviceId,
      reservationNumber,
      roomNumber,
      alternateReservationNumbers: alternateLocators,
      sectionCode: 'K',
      metadata: {
        roomNumber,
        qrCodeType: config.qrCodeType,
        checkInSource: config.checkInSource,
        keyType: 'physical',
        isWalkingGuest: isWalkingGuest.toString(),
      },
    });
    totalEvents++;
    successful++;

    addLog('success', `Key dispensed successfully | Room: ${roomNumber} | ${Date.now() - startTime}ms`);

    return {
      success: true,
      totalEvents,
      successful,
      failed,
      abandoned: 0,
      duration: Date.now() - startTime,
    };
  }, []);

  return { simulate };
};
